/**
 * Supabase auth session storage backed by Office **roaming settings**.
 *
 * Roaming settings live on the user's Exchange mailbox, scoped to this add-in,
 * so a session survives closing Outlook and follows the user to another device
 * — which localStorage (per-browser-profile, and wiped by the OWA iframe in
 * some hosts) does not.
 *
 * Two things about the Office API shape this has to work around:
 *
 *  1. `set()` only mutates an in-memory bag. Nothing persists until
 *     `saveAsync()` runs, so every write schedules a save. Saves are debounced
 *     because supabase-js writes the session on every token refresh and a
 *     save-per-write would round-trip to Exchange each time.
 *
 *  2. `get()` is synchronous but roaming settings only exist inside Outlook.
 *     Running `vite dev` in a plain browser, or any non-Outlook host, has no
 *     `Office.context.mailbox`, so we fall back to localStorage. The fallback
 *     is for development only — it is never the path a sideloaded add-in takes.
 *
 * Supabase's storage contract allows sync or async returns; we answer `getItem`
 * synchronously so the client can restore a session on first paint without a
 * flash of the login pane.
 */

const SAVE_DEBOUNCE_MS = 400;

function roaming(): Office.RoamingSettings | null {
  try {
    // `Office` is a CDN global; it is absent outside the add-in host.
    return typeof Office !== 'undefined' && Office.context?.roamingSettings
      ? Office.context.roamingSettings
      : null;
  } catch {
    return null;
  }
}

let saveTimer: ReturnType<typeof setTimeout> | null = null;

function scheduleSave(): void {
  const rs = roaming();
  if (!rs) return;
  if (saveTimer) clearTimeout(saveTimer);
  saveTimer = setTimeout(() => {
    saveTimer = null;
    rs.saveAsync((result) => {
      if (result.status === Office.AsyncResultStatus.Failed) {
        // Non-fatal: the in-memory value still works for this session, the user
        // just has to sign in again next time. Surfacing it would be noise.
        console.warn('roamingSettings.saveAsync failed:', result.error?.message);
      }
    });
  }, SAVE_DEBOUNCE_MS);
}

export const officeRoamingStorage = {
  getItem(key: string): string | null {
    const rs = roaming();
    if (rs) {
      const v = rs.get(key);
      return typeof v === 'string' ? v : null;
    }
    try {
      return window.localStorage.getItem(key);
    } catch {
      return null;
    }
  },

  setItem(key: string, value: string): void {
    const rs = roaming();
    if (rs) {
      rs.set(key, value);
      scheduleSave();
      return;
    }
    try {
      window.localStorage.setItem(key, value);
    } catch {
      /* private mode / storage disabled — session stays in memory only */
    }
  },

  removeItem(key: string): void {
    const rs = roaming();
    if (rs) {
      rs.remove(key);
      scheduleSave();
      return;
    }
    try {
      window.localStorage.removeItem(key);
    } catch {
      /* ignore */
    }
  },
};

/** True when running inside a real Office host (vs. a plain browser tab). */
export function isOfficeHost(): boolean {
  return roaming() !== null;
}
